import React from 'react'
import { Plane } from '@react-three/drei'
import { useDesignStore } from '../../store/designStore'
import BackgroundImagePlane from './BackgroundImagePlane'
import TerrainFromImage from './TerrainFromImage'
import CanvasBoundary from './CanvasBoundary'
import PanoramicBackground from './PanoramicBackground'
import CylindricalBackground from './CylindricalBackground'
import { RealisticGroundMaterial } from './materials/RealisticMaterials'

const GroundPlane: React.FC = () => {
  const { canvasSettings } = useDesignStore()

  return (
    <group>
      {/* Panoramic Background - renders behind everything */}
      {canvasSettings.backgroundImage && (
        <>
          {canvasSettings.backgroundImageMode === 'spherical' && <PanoramicBackground />}
          {canvasSettings.backgroundImageMode === 'cylindrical' && <CylindricalBackground />}
        </>
      )}

      {/* 3D Terrain from image (when enabled) */}
      {canvasSettings.enable3DTerrain && canvasSettings.backgroundImage ? (
        <TerrainFromImage 
          heightScale={canvasSettings.terrainHeightScale}
          segments={canvasSettings.terrainSegments}
          buildingAreaSize={canvasSettings.terrainBuildingAreaSize}
        />
      ) : (
        <>
          {/* Background Image Plane (rendered first, below ground) - only for flat mode */}
          {canvasSettings.backgroundImageMode === 'flat' && <BackgroundImagePlane />}
          
          {/* Main ground plane */}
          <Plane
            args={[canvasSettings.width, canvasSettings.height]}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, -0.1, 0]}
            receiveShadow
          >
            <RealisticGroundMaterial hasBackgroundImage={!!canvasSettings.backgroundImage} />
          </Plane>
        </>
      )}
      
      {/* Grid overlay */}
      {canvasSettings.showGrid && (
        <group position={[0, 0, 0]}>
          {/* Vertical grid lines */}
          {Array.from({ length: Math.floor(canvasSettings.width / canvasSettings.gridSize) + 1 }, (_, i) => {
            const x = (i * canvasSettings.gridSize) - (canvasSettings.width / 2)
            return (
              <Plane
                key={`v-${i}`}
                args={[0.05, canvasSettings.height]}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[x, -0.05, 0]}
              >
                <meshBasicMaterial color="#ffffff" transparent opacity={0.3} />
              </Plane>
            )
          })}
          
          {/* Horizontal grid lines */}
          {Array.from({ length: Math.floor(canvasSettings.height / canvasSettings.gridSize) + 1 }, (_, i) => {
            const z = (i * canvasSettings.gridSize) - (canvasSettings.height / 2)
            return (
              <Plane
                key={`h-${i}`}
                args={[canvasSettings.width, 0.05]}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, -0.05, z]}
              >
                <meshBasicMaterial color="#ffffff" transparent opacity={0.3} />
              </Plane>
            )
          }          )}
        </group>
      )}

      {/* Canvas boundary indicators */}
      <CanvasBoundary />
    </group>
  )
}

export default GroundPlane